import React from 'react';
import { Helmet } from 'react-helmet';
import {
  DIGITAL_LINKS,
  MTD_PHASES,
  PENALTY_EASEMENT,
  QUARTERLY_DEADLINES_2026_27,
  getNextQuarterlyDeadline,
} from '../../compliance/mtdFacts';

const MTDFactHub = () => {
    const phase1 = MTD_PHASES[0];
    const phase2 = MTD_PHASES[1];
    const firstQuarter = QUARTERLY_DEADLINES_2026_27[0];
    const nextQuarter = getNextQuarterlyDeadline();
    const nextQuarterSentence = nextQuarter
        ? ` The next 2026/27 quarterly update deadline is ${nextQuarter.label}.`
        : '';

    const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "Does MTD apply to profit or turnover?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `MTD is based on Gross Turnover, not profit. HMRC looks at your total sales (revenue) before any expenses are deducted. If your combined trading and property income was over £${phase1.thresholdGBP.toLocaleString('en-GB')} in your 2024/25 tax return, you are mandated from ${phase1.startDate}.`
                }
            },
            {
                "@type": "Question",
                "name": "Is Excel banned under MTD 2026?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": DIGITAL_LINKS.summary
                }
            },
            {
                "@type": "Question",
                "name": "Can I wait until January 2027 to worry about MTD?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `The first 2026/27 quarterly update deadline was ${firstQuarter.label}. Under MTD you must send quarterly updates through compatible software. Waiting until January means earlier quarterly deadlines have already passed.${nextQuarterSentence}`
                }
            },
            {
                "@type": "Question",
                "name": "Do I only need to report my annual profit under MTD?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `No. MTD for Income Tax requires quarterly updates through compatible software. The first 2026/27 deadline was ${firstQuarter.label}.${nextQuarterSentence}`
                }
            },
            {
                "@type": "Question",
                "name": "Can I type my totals into the HMRC website under MTD?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Quarterly updates and the tax return must be sent through compatible software, not by typing totals into the old Self Assessment pages. Typing original income and expense entries into that compatible software is allowed."
                }
            },
            {
                "@type": "Question",
                "name": "I'm under the £90k VAT threshold — am I exempt from MTD?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `No. MTD for Income Tax applies if your turnover is over £${phase1.thresholdGBP.toLocaleString('en-GB')} (dropping to £${phase2.thresholdGBP.toLocaleString('en-GB')} in 2027). VAT registration is a separate test.`
                }
            },
            {
                "@type": "Question",
                "name": "Will I get fined £200 if I miss a quarterly update in 2026?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": PENALTY_EASEMENT.description
                }
            }
        ]
    };

    const facts = [
        {
            id: "profit-myth",
            myth: "It only applies if I make over £50,000 in profit.",
            fact: `It is based on Gross Turnover, not profit. HMRC looks at your total sales (revenue) before expenses. If your combined trading and property income was over £${phase1.thresholdGBP.toLocaleString('en-GB')} in your 2024/25 tax return, you are mandated from 6 April 2026 — even if you operated at a loss.`,
            refUrl: phase1.source,
            refAlias: phase1.sourceAlias
        },
        {
            id: "excel-banned-myth",
            myth: "Excel is banned under MTD.",
            fact: DIGITAL_LINKS.summary,
            refUrl: DIGITAL_LINKS.source,
            refAlias: DIGITAL_LINKS.sourceAlias
        },
        {
            id: "wait-until-january-myth",
            myth: "I'll just wait until January 2027 to worry about it.",
            fact: `The first 2026/27 quarterly update deadline was ${firstQuarter.label}. Under MTD you must send quarterly updates through compatible software. Waiting until January means earlier quarterly deadlines have already passed.${nextQuarterSentence}`,
            refUrl: PENALTY_EASEMENT.source,
            refAlias: PENALTY_EASEMENT.sourceAlias
        },
        {
            id: "annual-report-myth",
            myth: "I only need to report my annual profit.",
            fact: `MTD for Income Tax requires quarterly updates through compatible software. The first 2026/27 deadline was ${firstQuarter.label}.${nextQuarterSentence}`,
            refUrl: PENALTY_EASEMENT.source,
            refAlias: PENALTY_EASEMENT.sourceAlias
        },
        {
            id: "hmrc-portal-myth",
            myth: "I can just type my totals into the HMRC website like before.",
            fact: "Quarterly updates and the tax return must be sent through compatible software, not by typing totals into the old Self Assessment pages. Typing original income and expense entries into that compatible software is allowed.",
            refUrl: DIGITAL_LINKS.source,
            refAlias: DIGITAL_LINKS.sourceAlias
        },
        {
            id: "vat-threshold-myth",
            myth: "I'm under the £90k VAT threshold, so I'm exempt.",
            fact: `MTD for Income Tax applies if your turnover is over £${phase1.thresholdGBP.toLocaleString('en-GB')} (dropping to £${phase2.thresholdGBP.toLocaleString('en-GB')} in 2027). VAT registration is a separate test.`,
            refUrl: phase1.source,
            refAlias: phase1.sourceAlias
        },
        {
            id: "fine-myth",
            myth: "If I miss a quarterly update this year, I'll get fined £200.",
            fact: PENALTY_EASEMENT.description,
            refUrl: PENALTY_EASEMENT.source,
            refAlias: PENALTY_EASEMENT.sourceAlias
        }
    ];

    return (
        <div id="mtd-myths" className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Helmet>
            <div className="mb-8 text-center">
                <div className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest text-emerald-400 uppercase bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-4">
                    Fact vs. Fiction
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">MTD 2026: Myths Debunked</h2>
                <p className="text-slate-400">Clearing up the biggest misconceptions about Making Tax Digital 2026.</p>
            </div>
            <div className="space-y-6">
                {facts.map((f) => (
                    <div key={f.id} id={f.id} className="bg-black/40 border border-white/5 p-6 rounded-xl">
                        <h3 className="text-lg font-bold text-red-400 mb-2">Fiction: "{f.myth}"</h3>
                        <p className="text-emerald-400 font-bold mb-3">Fact: {f.fact}</p>

                        <div className="text-[10px] text-slate-500 italic mt-3 mb-4 border-t border-white/5 pt-2">
                            Source: <a href={f.refUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">{f.refAlias}</a>
                        </div>

                        <a href="/#signup" className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors uppercase tracking-wider font-semibold">
                            Start capturing receipts with Zap →
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MTDFactHub;
